import React from 'react';
import PropTypes from 'prop-types';
import Masonry from 'react-responsive-masonry';
import { Button, Chip, CircularProgress } from '@mui/material';
import { Add, EmojiEmotions } from '@mui/icons-material';
import { Text, Title, ConfirmationDialog } from 'shared/components';
import { findIndex } from 'lodash';

import {
  Container,
  Header,
  Center,
  SotableItem,
  ContentCentered,
  Clearfix,
  Divider,
  Description
} from './styles';
import DashboardCard from '../../components/DashboardCard';
import TemplatesModal from '../../components/TemplatesModal';
import CardDetailsModal from '../../components/CardDetailsModal';
import DashboardSettingsPanel from '../../components/DashboardSettingsPanel';
import { TitleCard } from '../../components/styles';

/*
  Container: Renders a single dashboard
*/
const Dashboard = ({
  board,
  onSetCards,
  onSortCards,
  onClone,
  onUpdate,
  onDelete,
  onSetDefault,
  onEditMode,
  editMode,
  openSettingsPanel,
  loading,
  dashboards
}) => {
  const [openTemplateModal, setOpenTemplateModal] = React.useState(false);
  const [openDetailsModal, setOpenDetailsModal] = React.useState(false);
  const [selectedCard, setSelectedCard] = React.useState(null);
  const [openDeleteCardDialog, setOpenDeleteCardDialog] = React.useState(false);
  const [cardIdToDelete, setCardIdToDelete] = React.useState(null);
  const [dropId, setDropId] =  React.useState(null);

  const toggleTemplateModal = () => {
    setOpenTemplateModal(!openTemplateModal);
  };

  const toggleDetailsModal = () => {
    setOpenDetailsModal(!openDetailsModal);
  };

  const handleOnAddCard = card => {
    onSetCards(board, [card, ...board.cards]);
    toggleTemplateModal();
  };

  const handleOnAddCardNoToggle = card => {
    onSetCards(board, [card, ...board.cards]);
  };

  const handleOnCloneCard = card => {
    onSetCards(board, [card, ...board.cards]);
  };

  const handleOnDelete = id => {
    setCardIdToDelete(id);
    setOpenDeleteCardDialog(true);
  };

  const confirmOnDelete = () => {
    onSetCards(
      board,
      [...board.cards].filter(card => card.id !== cardIdToDelete)
    );
    setSelectedCard(null);
    setOpenDeleteCardDialog(false);
  };

  const handleOnOpenDetails = card => {
    setSelectedCard(card);
    toggleDetailsModal();
  };

  const toggleSettingsPanel = () => {
    onEditMode(!openSettingsPanel);
  };

  const handleClone = boardCopy => {
    onClone(boardCopy);
  };

  const handleUpdate = (boardCopy, name, description) => {
    onUpdate(boardCopy, name, description);
  };

  const handleDeleteDashboard = boardCopy => {
    onDelete(boardCopy);
  };

  const handleUpdateCardSettings = updatedCard => {
    const boardCopy = { ...board };
    boardCopy.cards.forEach((card, idx) => {
      if (card.id === updatedCard.id) {
        boardCopy.cards[idx] = updatedCard;
      }
    });
    onUpdate(boardCopy, board.name, board.description);
  };

  const arrayMove = (arr, oldIndex, newIndex) => {
    if (newIndex >= arr.length) {
        let k = newIndex - arr.length + 1;
        while (k--) { arr.push(undefined); }
    }
    arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
    return arr;
  };

  const onDragOver = (id) => {
    setDropId(id);
  };

  const onDragEnd = (draggedId) => {
    const cards = [...board.cards];
    const oldIndex = findIndex(cards, (e) => e.id === draggedId);
    const newIndex = findIndex(cards, (e) => e.id === dropId);
    const cardsSorted = arrayMove(cards, oldIndex, newIndex);
    onSortCards(board.id, cardsSorted);
  };

  return (
    <Container elevation={0}>
      <Header>
        {loading && <CircularProgress size={25} />}
        {!loading && (
          <>
            <TitleCard>{board.name}</TitleCard>
            <Clearfix />
            <Description>
              <Text>
                <span style={{maxWidth: 'calc(100% - 133px)', float: 'left'}}>{board.description}</span>
                <Button
                  variant="outlined"
                  onClick={toggleTemplateModal}
                  endIcon={<Add />}
                  disabled={editMode}
                  style={{float: 'right', bottom: '20px'}}
                >
                  Add Card
                </Button>
                <div style={{clear: 'both'}}></div>
              </Text>
            </Description>
          </>
        )}
        <Divider style={{ marginBottom: 20, marginTop: 15 }}/>
        { openSettingsPanel && (
          <DashboardSettingsPanel
            open={openSettingsPanel}
            board={board}
            onClose={toggleSettingsPanel}
            onClone={handleClone}
            onSave={handleUpdate}
            onDelete={handleDeleteDashboard}
            onSetDefault={onSetDefault}
            dashboards={dashboards}
          />
        )}
        <Center>
          {board.cards.length === 0 && (
            <ContentCentered>
              <Chip
                color="primary"
                label="No cards yet, add a new card now!"
                onClick={toggleTemplateModal}
                onDelete={toggleTemplateModal}
                deleteIcon={<EmojiEmotions />}
                variant="outlined"
                clickable
              />
            </ContentCentered>
          )}
          {board.cards.length > 0 && (
            <Masonry columnsCount={2}>
              {board.cards.map(card => (
                <SotableItem
                  key={card.id}
                  onDragOver={(e) => onDragOver(card.id)}
                  onDragEnd={(e) => onDragEnd(card.id)}
                  draggable>
                    <DashboardCard
                      isEditing={openSettingsPanel}
                      title={card.title}
                      onClickDetails={() => handleOnOpenDetails(card)}
                      selectedCard={card}
                      onDelete={handleOnDelete}
                      onClone={handleOnCloneCard}
                      onUpdateSettings={handleUpdateCardSettings}
                      onAddCard={handleOnAddCardNoToggle}
                      onClose={toggleDetailsModal}
                    />
                </SotableItem>
              ))}
            </Masonry>
          )}
          <Clearfix />
        </Center>
      </Header>
      <TemplatesModal
        onAddCard={handleOnAddCard}
        open={openTemplateModal}
        onClose={toggleTemplateModal}
      />
      <CardDetailsModal
        card={selectedCard}
        open={openDetailsModal}
        onClose={toggleDetailsModal}
      />
      <ConfirmationDialog
        openDialog={openDeleteCardDialog}
        onCloseDialog={setOpenDeleteCardDialog}
        actionButtonOne={confirmOnDelete}
        actionButtonTwo={setOpenDeleteCardDialog}
        textButtonOne="Delete"
        textButtonTwo="Cancel"
        title="Delete this card?"
        IdToDelete={selectedCard}
      />
    </Container>
  );
};

Dashboard.propTypes = {
  board: PropTypes.object,
  onSetCards: PropTypes.func,
  onSortCards: PropTypes.func,
  onClone: PropTypes.func,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onSetDefault: PropTypes.func,
  onEditMode: PropTypes.func,
  editMode: PropTypes.bool,
  openSettingsPanel: PropTypes.bool,
  loading: PropTypes.bool,
  dashboards: PropTypes.array
};

export default React.memo(Dashboard);
